import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";

import { vibrate } from "./cardCommon";
import { VideoTextBlock } from "./VideoTextBlock";

/**
 * Video card. Autoplays muted (browser policy) when the card becomes
 * active; tap-to-unmute via a clear icon overlay shown briefly the first
 * time. Two layouts, driven by `card.layout_variant` (mirrors the
 * image+text card):
 *
 *   - `full_bleed` (default) — video fills the card (`object-cover`, so a
 *     landscape clip is center-cropped), title + caption overlaid with a
 *     gradient at `text_position`. The TikTok-y default.
 *   - `split`               — video shown in its NATIVE aspect at the top of
 *     the card (the region is sized to the clip's real aspect ratio so a
 *     landscape clip sits flush with no black bars), title + caption on a
 *     white region below. The point of split is to show the whole frame.
 *
 * Mute is controllable: pass `isMuted` + `onToggleMute` to lift the state to
 * the deck player (so one unmute sticks across every clip in the deck).
 * Omit them and the card falls back to its own local mute state (used by the
 * standalone preview tiles).
 *
 * v1 ships without server-rendered captions: the BE serializer doesn't
 * resolve `captions_vtt_uuid` references inside content_json, and the
 * editor-side guidance is to burn captions into the video. When proper
 * VTT support lands, this component becomes the only place that needs
 * to add a `<track>`.
 */
// Height reserved at the top of the SPLIT layout so the player's progress bar
// + close X sit on a clean black band ABOVE the video, rather than overlaid on
// it. The chrome bottom (32px close button at pt-3) is ≈44px; this leaves a
// comfortable ~20px gap below it before the video. Full-bleed keeps the chrome
// overlaid (intended TikTok look).
const SPLIT_TOP_CHROME_PX = 64;

export function VideoCard({
  card,
  isActive,
  shouldLoadMedia = true,
  onAdvance,
  isMuted,
  onToggleMute,
  onProgress,
}) {
  const videoRef = React.useRef(null);
  // Mute is controlled when the player passes isMuted/onToggleMute (shared
  // across the deck); otherwise this card owns it (preview tiles).
  const isControlledMute = isMuted !== undefined;
  const [localMuted, setLocalMuted] = React.useState(true);
  const muted = isControlledMute ? isMuted : localMuted;
  const [showControlsHint, setShowControlsHint] = React.useState(false);
  const [hasInteracted, setHasInteracted] = React.useState(false);
  // User-initiated pause (tap the video). Distinct from the play/pause that
  // tracks `isActive` — this is the viewer deliberately stopping to read/absorb.
  const [isPaused, setIsPaused] = React.useState(false);
  // Mirror of isPaused for the async readiness retries (canplay/loadeddata):
  // those fire after the play effect's closure was captured, so they can't read
  // the latest isPaused state — they read this ref instead, to avoid resuming a
  // clip the viewer deliberately paused while it was still buffering.
  const isPausedRef = React.useRef(false);
  React.useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  // True while the read-more sheet is open. The sheet pauses the video itself
  // (VideoTextBlock.open) and resumes it on close, so readiness retries must be
  // suppressed too — otherwise a canplay/loadeddata that fires while the learner
  // is reading would resume playback underneath the sheet.
  const sheetOpenRef = React.useRef(false);
  // Tap-vs-swipe: record where a pointer went down; only treat the release as
  // a tap (toggle play/pause) if it barely moved, so swiping to the next card
  // doesn't pause. The swipe itself still reaches the player's Embla viewport.
  const pointerStartRef = React.useRef(null);
  // The clip's real aspect ratio (width / height), read once metadata loads.
  // Drives the split layout's video region so it fits with no black bars.
  const [videoAspect, setVideoAspect] = React.useState(null);

  const layout = card.layout_variant || "full_bleed";
  const isSplit = layout === "split";
  const content = card.content || {};
  const title = content.title || "";
  const caption = content.caption || "";
  // Typography options (emoji prefix, sizes, bold/italic) — parity with the
  // image+text card. Forwarded to VideoTextBlock, which owns the defaults.
  const textProps = {
    emoji: content.emoji || null,
    titleSize: content.title_size,
    captionSize: content.caption_size,
    titleBold: content.title_bold,
    titleItalic: content.title_italic,
  };
  const textAlign = content.text_align || "left";
  const textPosition = content.text_position || "bottom";
  const textAlignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[textAlign];
  const videoUrl = shouldLoadMedia ? card.media?.url || null : null;
  const thumbnailUrl = shouldLoadMedia ? card.thumbnail?.url || null : null;
  // A bound clip with no URL yet is still being cut/optimized on the worker —
  // show a processing state rather than the "broken" Video unavailable copy.
  const mediaStatus = card.media?.video_status || null;
  const isProcessing =
    !videoUrl &&
    shouldLoadMedia &&
    (mediaStatus === "processing" || mediaStatus === "pending");

  // Where the title + caption block sits, and the gradient that sits
  // behind them for legibility. Mirrors the full-bleed image+text card
  // so admins see consistent positioning behavior across card types.
  const overlayContainerClass = {
    top: "absolute top-0 left-0 right-0 pt-16 pb-16 px-6",
    middle: "absolute inset-0 flex flex-col justify-center px-6",
    bottom: "absolute bottom-0 left-0 right-0 pt-16 pb-12 px-6",
  }[textPosition];
  const overlayGradient = {
    top: "linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)",
    middle: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55))",
    bottom:
      "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)",
  }[textPosition];

  // Play / pause based on whether this card is the active one. `videoUrl`
  // is included so a video whose signed URL was refreshed after activation
  // starts as soon as the refreshed media element mounts.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return undefined;
    if (!isActive) {
      v.pause();
      return undefined;
    }
    // Becoming active. While this card was just a neighbor it only had
    // preload="metadata", so the media usually isn't buffered enough to begin
    // the instant isActive flips — especially when swiping from one video
    // straight to the next. A single play() call then stalls on a black frame
    // (the poster is hidden once the seek starts) and the viewer has to tap to
    // kick it off. Retry play() on the readiness events so a fresh video card
    // always starts on its own; a muted fallback covers an autoplay-policy
    // rejection (matches the muted-autoplay contract the deck opens with).
    let cancelled = false;
    // A freshly activated card always starts playing. Reset the pause + sheet
    // refs synchronously (the [isPaused] sync effect hasn't run yet, and the
    // read-more sheet auto-collapses on deactivation without firing its
    // callback) so neither a stale pause nor a stale sheet flag from a prior
    // activation can suppress this card's first play.
    isPausedRef.current = false;
    sheetOpenRef.current = false;
    try {
      v.currentTime = 0;
    } catch (e) {
      /* seeking before metadata is loaded throws on some browsers; ignore */
    }
    const attempt = () => {
      // Skip if torn down, no longer the active card, the viewer has explicitly
      // paused (a readiness retry must not resume a user pause and leave the
      // pause overlay up while the video plays), or the read-more sheet is open
      // (it would resume playback underneath the sheet the learner is reading).
      if (cancelled || !isActive || isPausedRef.current || sheetOpenRef.current)
        return;
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          if (
            cancelled ||
            !isActive ||
            v.muted ||
            isPausedRef.current ||
            sheetOpenRef.current
          )
            return;
          // Autoplay with sound is blocked outside a user gesture. Mute so the
          // clip still rolls — but keep the React mute state in sync too, or the
          // control would show "sound on" while audio is off and the next tap
          // would re-mute instead of restoring sound. We only get here while
          // currently unmuted, so flipping to muted is always the right move:
          // controlled → toggle the deck's shared isMuted; otherwise local.
          v.muted = true;
          if (isControlledMute) {
            onToggleMute && onToggleMute();
          } else {
            setLocalMuted(true);
          }
          const p2 = v.play();
          if (p2 && typeof p2.catch === "function") p2.catch(() => {});
        });
      }
    };
    attempt();
    v.addEventListener("canplay", attempt);
    v.addEventListener("loadeddata", attempt);
    vibrate(8);
    setIsPaused(false); // a freshly activated card always starts playing
    return () => {
      cancelled = true;
      v.removeEventListener("canplay", attempt);
      v.removeEventListener("loadeddata", attempt);
    };
  }, [isActive, videoUrl]);

  // Keep the media element's muted property in sync with `muted`. Done
  // imperatively (React's `muted` attribute is unreliable) so a remount or a
  // shared-state change from another card both take effect.
  React.useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, videoUrl]);

  // First-time unmute hint — separate from the play effect so flipping
  // hasInteracted (via the mute toggle) doesn't restart the video. Suppressed
  // once unmuted (in controlled mode that means it never re-shows after the
  // first unmute anywhere in the deck).
  React.useEffect(() => {
    if (!isActive || hasInteracted || !muted) return undefined;
    setShowControlsHint(true);
    const t = setTimeout(() => setShowControlsHint(false), 2500);
    return () => clearTimeout(t);
  }, [isActive, hasInteracted, muted]);

  const handleVideoEnded = () => {
    onAdvance && onAdvance();
  };

  const handleToggleMute = () => {
    if (isControlledMute) {
      onToggleMute && onToggleMute();
    } else {
      setLocalMuted((prev) => !prev);
    }
    setHasInteracted(true);
    setShowControlsHint(false);
    vibrate(10);
  };

  // Tap the video to pause/resume (TikTok / Reels behavior). Resuming from a
  // user pause must NOT reset to 0, so this toggles play/pause directly rather
  // than going through the isActive effect.
  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    if (v.paused) {
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
      // Update the ref synchronously too: a readiness retry (canplay/loadeddata)
      // can fire before this state change re-renders, and it reads the ref.
      isPausedRef.current = false;
      setIsPaused(false);
    } else {
      v.pause();
      isPausedRef.current = true;
      setIsPaused(true);
    }
    vibrate(6);
  };

  // Spread onto whichever element holds the video. Records the pointer-down
  // point; on release, only counts as a tap (toggle) if it barely moved, so a
  // swipe to the next card isn't read as a pause. Embla still gets the drag.
  const videoTapHandlers = {
    onPointerDown: (e) => {
      pointerStartRef.current = { x: e.clientX, y: e.clientY };
    },
    onPointerUp: (e) => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start) return;
      const moved =
        Math.abs(e.clientX - start.x) > 10 ||
        Math.abs(e.clientY - start.y) > 10;
      if (!moved) togglePlayPause();
    },
  };

  // Centered play glyph shown while the active card is user-paused, so it
  // reads as paused (not frozen) and obviously resumable. pointer-events-none
  // so the resume tap falls through to the region's tap handler.
  const playOverlay =
    isActive && isPaused ? (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <div className="w-16 h-16 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center">
          <PlayArrowIcon style={{ fontSize: 40 }} className="text-white" />
        </div>
      </div>
    ) : null;

  // Media controller handed to VideoTextBlock so the read-more sheet can
  // pause/resume the clip without VideoTextBlock needing to know it's a native
  // <video> (the embed card passes a provider-SDK controller of the same shape).
  const nativeController = React.useMemo(
    () => ({
      isPlaying: () => {
        const v = videoRef.current;
        return !!(v && !v.paused);
      },
      pause: () => videoRef.current?.pause(),
      play: () => {
        const v = videoRef.current;
        if (v && videoUrl) {
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      },
    }),
    [videoUrl]
  );

  // Placeholder shown IN the media region when there's no playable clip yet, so
  // the layout + text still render (like the image+text card) instead of the
  // whole card being taken over. Only for the active/near card (shouldLoadMedia);
  // neighbors render an empty black region. "Video unavailable" only when a clip
  // was actually bound but can't play; a fresh card gets a neutral "No video yet".
  const placeholderEl =
    !videoUrl && shouldLoadMedia ? (
      isProcessing ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black text-white">
          <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          <span className="text-sm opacity-80">Processing video…</span>
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black text-white/60">
          <OndemandVideoOutlinedIcon style={{ fontSize: 40 }} />
          <span className="text-sm">
            {card.media?.uuid || card.media?.url
              ? "Video unavailable"
              : "No video yet"}
          </span>
        </div>
      )
    ) : null;

  // The <video> element. full_bleed crops to fill (object-cover); split shows
  // the whole frame (object-contain) inside a region sized to the clip's real
  // aspect, so neither path letterboxes.
  const videoEl = videoUrl ? (
    <video
      ref={videoRef}
      src={videoUrl}
      poster={thumbnailUrl || undefined}
      muted={muted}
      playsInline
      preload={isActive ? "auto" : "metadata"}
      onEnded={handleVideoEnded}
      onTimeUpdate={(e) => {
        // Drives the active segment of the player's top progress bar, so the
        // viewer can see how far through the clip they are (Instagram Stories
        // style). Only the active card is wired (player passes onProgress).
        const v = e.target;
        if (onProgress && v.duration > 0) {
          onProgress(Math.min(1, v.currentTime / v.duration));
        }
      }}
      onLoadedMetadata={(e) => {
        const v = e.target;
        if (v && v.videoWidth && v.videoHeight) {
          setVideoAspect(v.videoWidth / v.videoHeight);
        }
      }}
      className={
        isSplit
          ? "w-full h-full object-contain"
          : "absolute inset-0 w-full h-full object-cover"
      }
    />
  ) : null;

  // The clip if we have one, otherwise the placeholder (so the layout + text
  // render either way). hasVideo gates the playback-only chrome (mute toggle).
  const hasVideo = Boolean(videoUrl);
  const mediaEl = videoEl || placeholderEl;

  // Mute toggle + first-time hint. Positioned relative to whichever region
  // holds the video (the whole card in full_bleed; the top region in split),
  // so the helper takes the offset classes from the caller.
  const muteControls = (btnPos, hintPos) => (
    <>
      <button
        type="button"
        onClick={handleToggleMute}
        // Keep mute taps from bubbling to the video region's tap handler (which
        // would also pause/resume).
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        aria-label={muted ? "Unmute" : "Mute"}
        className={`absolute ${btnPos} w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors z-20`}
      >
        {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
      </button>
      <AnimatePresence>
        {showControlsHint && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`absolute ${hintPos} px-3 py-2 rounded-lg bg-black/70 backdrop-blur-sm text-white text-xs z-10`}
          >
            Tap to unmute
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Split layout — video (native aspect, no bars) on top, text on white below

  if (isSplit) {
    return (
      <div className="relative w-full h-full flex flex-col bg-black select-none">
        {/* Black band that holds the player's progress bar + close X, so the
            (short, landscape) clip sits cleanly below them rather than under
            overlaid chrome. */}
        <div className="shrink-0" style={{ height: SPLIT_TOP_CHROME_PX }} />
        {/* Sized to the clip's real aspect so a landscape clip sits flush with
            no letterboxing; capped so a portrait clip can't eat the whole card
            (the text region needs room). Defaults to 16:9 before metadata. */}
        <div
          className="relative bg-black overflow-hidden w-full shrink-0"
          style={{ aspectRatio: videoAspect || 16 / 9, maxHeight: "55%" }}
          {...videoTapHandlers}
        >
          {mediaEl}
          {playOverlay}
          {/* The chrome now sits on the spacer above, so the mute button can
              hug the top-right of the video region itself. */}
          {hasVideo && muteControls("top-3 right-3", "top-14 right-3")}
        </div>
        <div className={`flex-1 relative bg-white min-h-0 ${textAlignClass}`}>
          <div className="absolute inset-0 overflow-hidden flex flex-col justify-start px-6 pt-8 pb-8">
            <VideoTextBlock
              title={title}
              caption={caption}
              onWhite
              {...textProps}
              mediaController={nativeController}
              isActive={isActive}
              onSheetOpenChange={(open) => {
                sheetOpenRef.current = open;
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Full-bleed layout (default)

  return (
    <div
      className="relative w-full h-full overflow-hidden bg-black select-none"
      {...videoTapHandlers}
    >
      {mediaEl}
      {playOverlay}

      {/* Title + caption overlay — position and gradient depend on
          card.content.text_position. Renders even with just one of the
          two; the gradient sits behind for legibility against any video. */}
      {(title || caption) && (
        <div
          className={`${overlayContainerClass} text-white pointer-events-none ${textAlignClass}`}
          style={{ background: overlayGradient }}
        >
          <VideoTextBlock
            title={title}
            caption={caption}
            {...textProps}
            mediaController={nativeController}
            isActive={isActive}
            onSheetOpenChange={(open) => {
              sheetOpenRef.current = open;
            }}
          />
        </div>
      )}

      {hasVideo && muteControls("top-20 right-4", "top-32 right-4")}
    </div>
  );
}
